/**
 * Data Cleanup Utilities
 *
 * Scripts to find and clean up orphaned data in Firebase
 * Run these manually to fix data consistency issues
 */

import { collection, getDocs, writeBatch, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Find orphaned investments (investments where accountId doesn't match any existing account)
 *
 * @param {string} userId - The user's Firebase UID
 * @returns {Promise<Object>} Report of orphaned investments found
 */
export async function findOrphanedInvestments(userId) {
  try {
    console.log('[Cleanup] Starting orphaned investment scan for user:', userId);

    // 1. Get all accounts from subcollection
    const accountsRef = collection(db, 'users', userId, 'accounts');
    const accountsSnapshot = await getDocs(accountsRef);
    const accountIds = new Set();

    accountsSnapshot.forEach((doc) => {
      accountIds.add(doc.id);
      console.log(`[Cleanup] Found account ID: ${doc.id}`);
    });

    console.log(`[Cleanup] Total accounts found: ${accountIds.size}`);
    console.log(`[Cleanup] Valid account IDs:`, Array.from(accountIds));

    // 2. Get all investments from user profile document (not subcollection!)
    const userProfileRef = doc(db, 'users', userId);
    const userProfileSnap = await getDoc(userProfileRef);

    let investments = [];
    if (userProfileSnap.exists()) {
      const profileData = userProfileSnap.data();
      investments = profileData.investments || [];
      console.log(`[Cleanup] Found ${investments.length} investments in user profile`);

      // Log first few investments for debugging
      if (investments.length > 0) {
        console.log('[Cleanup] Sample investment structure:', {
          id: investments[0].id,
          name: investments[0].name,
          accountId: investments[0].accountId,
          amount: investments[0].amount
        });
      }
    } else {
      console.log('[Cleanup] No user profile found');
    }

    const orphanedInvestments = [];
    let totalInvestments = investments.length;

    investments.forEach((investment, index) => {
      const investmentAccountId = investment.accountId;

      // Log each investment check
      if (index < 5) { // Log first 5 for debugging
        console.log(`[Cleanup] Checking investment ${index + 1}: ${investment.name} (ID: ${investment.id}), accountId: ${investmentAccountId}, exists: ${accountIds.has(investmentAccountId)}`);
      }

      // Check if investment has an accountId and if that account exists
      if (investmentAccountId && !accountIds.has(investmentAccountId)) {
        console.log(`[Cleanup] ⚠️ ORPHANED: ${investment.name} (${investment.id}) - accountId "${investmentAccountId}" does not exist`);
        orphanedInvestments.push(investment);
      }
    });

    console.log(`[Cleanup] Found ${orphanedInvestments.length} orphaned investments out of ${totalInvestments} total`);

    // Calculate total value of orphaned investments
    const orphanedValue = orphanedInvestments.reduce((sum, inv) =>
      sum + (parseFloat(inv.amount) || 0), 0
    );

    const report = {
      totalInvestments,
      totalAccounts: accountIds.size,
      orphanedCount: orphanedInvestments.length,
      orphanedValue,
      orphanedInvestments: orphanedInvestments.map(inv => ({
        id: inv.id,
        name: inv.name,
        ticker: inv.ticker,
        amount: inv.amount,
        accountId: inv.accountId,
        category: inv.category
      }))
    };

    return report;
  } catch (error) {
    console.error('[Cleanup] Error finding orphaned investments:', error);
    throw error;
  }
}

/**
 * Delete orphaned investments
 * ⚠️  WARNING: This permanently deletes data. Use with caution!
 *
 * @param {string} userId - The user's Firebase UID
 * @param {boolean} dryRun - If true, just report what would be deleted without deleting
 * @returns {Promise<Object>} Report of deletions performed
 */
export async function deleteOrphanedInvestments(userId, dryRun = true) {
  try {
    console.log(`[Cleanup] ${dryRun ? 'DRY RUN - ' : ''}Starting orphaned investment deletion for user:`, userId);

    // Find orphaned investments first
    const report = await findOrphanedInvestments(userId);

    if (report.orphanedCount === 0) {
      console.log('[Cleanup] No orphaned investments found. Database is clean!');
      return {
        ...report,
        deleted: 0,
        dryRun
      };
    }

    if (dryRun) {
      console.log(`[Cleanup] DRY RUN - Would delete ${report.orphanedCount} orphaned investments`);
      console.log('[Cleanup] Set dryRun=false to actually delete');
      return {
        ...report,
        deleted: 0,
        dryRun: true
      };
    }

    // Actually delete orphaned investments from user profile document
    console.log(`[Cleanup] Deleting ${report.orphanedCount} orphaned investments...`);

    // Get current user profile
    const userProfileRef = doc(db, 'users', userId);
    const userProfileSnap = await getDoc(userProfileRef);

    if (!userProfileSnap.exists()) {
      throw new Error('User profile not found');
    }

    const profileData = userProfileSnap.data();
    const currentInvestments = profileData.investments || [];

    // Create a Set of orphaned investment IDs for fast lookup
    const orphanedIds = new Set(report.orphanedInvestments.map(inv => inv.id));

    // Filter out orphaned investments
    const cleanedInvestments = currentInvestments.filter(inv => {
      const isOrphaned = orphanedIds.has(inv.id);
      if (isOrphaned) {
        console.log(`[Cleanup] Removing orphaned investment: ${inv.name} (${inv.id})`);
      }
      return !isOrphaned;
    });

    const deletedCount = currentInvestments.length - cleanedInvestments.length;

    // Update user profile with cleaned investments
    await setDoc(userProfileRef, {
      investments: cleanedInvestments
    }, { merge: true });

    console.log(`[Cleanup] Successfully deleted ${deletedCount} orphaned investments`);

    return {
      ...report,
      deleted: deletedCount,
      dryRun: false
    };
  } catch (error) {
    console.error('[Cleanup] Error deleting orphaned investments:', error);
    throw error;
  }
}

/**
 * Find all data integrity issues
 * Comprehensive check for various orphaned data types
 *
 * @param {string} userId - The user's Firebase UID
 * @returns {Promise<Object>} Complete integrity report
 */
export async function checkDataIntegrity(userId) {
  try {
    console.log('[Cleanup] Starting comprehensive data integrity check for user:', userId);

    const report = {
      timestamp: new Date().toISOString(),
      userId,
      issues: []
    };

    // Check orphaned investments
    const investmentReport = await findOrphanedInvestments(userId);
    if (investmentReport.orphanedCount > 0) {
      report.issues.push({
        type: 'orphaned_investments',
        count: investmentReport.orphanedCount,
        details: investmentReport
      });
    }

    // TODO: Add more integrity checks here as needed:
    // - Orphaned goals linked to deleted accounts
    // - Liabilities with invalid references
    // - Crypto holdings with invalid data
    // - etc.

    report.healthy = report.issues.length === 0;
    report.issueCount = report.issues.length;

    if (report.healthy) {
      console.log('[Cleanup] ✅ Data integrity check passed - no issues found!');
    } else {
      console.warn(`[Cleanup] ⚠️  Found ${report.issueCount} data integrity issue(s)`);
    }

    return report;
  } catch (error) {
    console.error('[Cleanup] Error checking data integrity:', error);
    throw error;
  }
}

/**
 * Helper function to format cleanup report for display
 */
export function formatCleanupReport(report) {
  if (!report) return 'No report available';

  let output = '═══════════════════════════════════════\n';
  output += '    DATA CLEANUP REPORT\n';
  output += '═══════════════════════════════════════\n\n';

  if (report.timestamp) {
    output += `Scan Time: ${new Date(report.timestamp).toLocaleString()}\n`;
    output += `User ID: ${report.userId}\n\n`;
  }

  output += `Total Investments: ${report.totalInvestments}\n`;
  output += `Total Accounts: ${report.totalAccounts}\n`;
  output += `Orphaned Investments: ${report.orphanedCount}\n`;

  if (report.orphanedValue > 0) {
    output += `Orphaned Value: $${report.orphanedValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
  }

  if (report.dryRun !== undefined) {
    output += `\nMode: ${report.dryRun ? 'DRY RUN (no changes made)' : 'ACTUAL DELETION'}\n`;
    if (report.deleted > 0) {
      output += `Deleted: ${report.deleted} investment(s)\n`;
    }
  }

  if (report.orphanedInvestments && report.orphanedInvestments.length > 0) {
    output += '\n───────────────────────────────────────\n';
    output += 'ORPHANED INVESTMENTS:\n';
    output += '───────────────────────────────────────\n';

    report.orphanedInvestments.forEach((inv, index) => {
      output += `\n${index + 1}. ${inv.name || 'Unknown'} (${inv.ticker || 'N/A'})\n`;
      output += `   Amount: $${(inv.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
      output += `   Category: ${inv.category || 'Unknown'}\n`;
      output += `   Missing Account ID: ${inv.accountId}\n`;
      output += `   Investment ID: ${inv.id}\n`;
    });
  }

  output += '\n═══════════════════════════════════════\n';

  return output;
}

export default {
  findOrphanedInvestments,
  deleteOrphanedInvestments,
  checkDataIntegrity,
  formatCleanupReport
};
